import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../Button/Button.styles';
import { FieldWrap, Label, Hint } from '../Field/Field.styles';
import { FieldError } from '../Field/Field';
import { Tile, TileIcon, TileTitle, TileHint } from '../ImageDropzone/ImageDropzone.styles';
import { ACCEPT_ANY_IMAGE } from '../../onboardingV2.images';
import { Grid, Cell, MainBadge, RemoveBtn, AddCell, Busy } from './MultiImageDropzone.styles';

/**
 * Several optional photos (the first one is the main photo). Any image format is accepted here;
 * the parent converts each file (normalizeImageFile) before adding it to `photos`.
 */
const MultiImageDropzone = ({
    id,
    label,
    hint,
    photos = [],
    max = 3,
    processing = 0,
    disabled = false,
    error,
    onFiles,
    onRemove,
}) => {
    const { t } = useTranslation();
    const slots = max - photos.length - processing;
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        multiple: true,
        noClick: true,
        noKeyboard: true,
        disabled: disabled || slots <= 0,
        onDrop: (files) => {
            if (files && files.length) onFiles(files);
        },
    });

    const labelId = `${id}-label`;
    const hintId = `${id}-hint`;
    const describedBy = [error ? `${id}-error` : null, hintId].filter(Boolean).join(' ');
    const empty = photos.length === 0 && processing === 0;

    return (
        <FieldWrap>
            <Label as="span" id={labelId}>
                {label} <small>{t('SignUpV2.common.optional')}</small>
            </Label>
            <div {...getRootProps()}>
                <input {...getInputProps()} id={id} accept={ACCEPT_ANY_IMAGE} />
                {empty ? (
                    <Tile
                        type="button"
                        onClick={open}
                        disabled={disabled}
                        dragActive={isDragActive}
                        invalid={!!error}
                        aria-labelledby={labelId}
                        aria-describedby={describedBy}
                    >
                        <TileIcon aria-hidden="true">
                            <i className="ri-image-add-line" />
                        </TileIcon>
                        <TileTitle aria-hidden="true">{t('SignUpV2.producto.photosAdd')}</TileTitle>
                        <TileHint aria-hidden="true">{t('SignUpV2.producto.photosDrag')}</TileHint>
                    </Tile>
                ) : (
                    <Grid dragActive={isDragActive} aria-labelledby={labelId}>
                        {photos.map((photo, index) => (
                            <Cell key={photo.id}>
                                <img
                                    src={photo.previewUrl}
                                    alt={t('SignUpV2.producto.photoAltN', { n: index + 1 })}
                                />
                                {index === 0 && <MainBadge>{t('SignUpV2.producto.photoMain')}</MainBadge>}
                                <RemoveBtn
                                    type="button"
                                    onClick={() => onRemove(photo.id)}
                                    disabled={disabled}
                                    aria-label={t('SignUpV2.producto.photoRemoveN', { n: index + 1 })}
                                >
                                    <i className="ri-close-line" aria-hidden="true" />
                                </RemoveBtn>
                            </Cell>
                        ))}
                        {Array.from({ length: processing }).map((_, i) => (
                            <Cell key={`busy-${i}`} as="div" aria-hidden="true">
                                <Busy>
                                    <Spinner className="ri-loader-4-line" />
                                    <span>{t('SignUpV2.producto.photoOptimizing')}</span>
                                </Busy>
                            </Cell>
                        ))}
                        {slots > 0 && (
                            <AddCell
                                type="button"
                                onClick={open}
                                disabled={disabled}
                                aria-describedby={describedBy}
                            >
                                <i className="ri-add-line" aria-hidden="true" />
                                <span>{t('SignUpV2.producto.photoAddMore')}</span>
                            </AddCell>
                        )}
                    </Grid>
                )}
            </div>
            <Hint id={hintId}>{hint}</Hint>
            <FieldError id={id} error={error} />
        </FieldWrap>
    );
};

export default MultiImageDropzone;
